import { createAsyncThunk, createEntityAdapter, createSlice, PayloadAction } from "@reduxjs/toolkit";
import agent from "../../app/api/agent";
import { Book, BookParams } from "../../app/models/book";
import { MetaData } from "../../app/models/pagination";
import { RootState } from "../../app/store/configureStore";

interface CatalogState {
  initialLoad: boolean;
  booksLoaded: boolean;
  filtersLoaded: boolean;
  status: string;
  bookFormats: string[];
  bookTypes: string[];
  genres: string[];
  languages: string[];
  targetAudiences: string[];
  countries: string[];
  yearsOfPublication: string[];
  bookParams: BookParams;
  metaData: MetaData | null;
}

interface PaginationParams {
  pageNumber: number;
}

const booksAdapter = createEntityAdapter<Book>();

const getAxiosParams = (bookParams: BookParams) => {
  const params = new URLSearchParams();
  params.append('pageNumber', bookParams.pageNumber.toString());
  params.append('pageSize', bookParams.pageSize.toString());

  if (bookParams.searchTerm) {
    params.append('searchTerm', bookParams.searchTerm);
  }

  if (bookParams.bookFormats.length > 0) {
    params.append('bookFormats', bookParams.bookFormats.toString());
  }

  if (bookParams.bookTypes.length > 0) {
    params.append('bookTypes', bookParams.bookTypes.toString());
  }

  if (bookParams.genres.length > 0) {
    params.append('genres', bookParams.genres.toString());
  }

  if (bookParams.languages.length > 0) {
    params.append('languages', bookParams.languages.toString());
  }

  if (bookParams.targetAudiences.length > 0) {
    params.append('targetAudiences', bookParams.targetAudiences.toString());
  }

  if (bookParams.countries.length > 0) {
    params.append('countries', bookParams.countries.toString());
  }

  if (bookParams.yearsOfPublication.length > 0) {
    params.append('yearsOfPublication', bookParams.yearsOfPublication.toString());
  }

  return params;
}

export const fetchBooksAsync = createAsyncThunk<Book[], void, { state: RootState }>(
  'catalog/fetchBooksAsync',
  async (_, thunkAPI) => {
    const params = getAxiosParams(thunkAPI.getState().catalog.bookParams);
    try {
      const response = await agent.Catalog.list(params);
      thunkAPI.dispatch(setMetaData(response.metaData));
      return response.items.items;
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ error: error.data });
    }
  }
)

export const fetchBookAsync = createAsyncThunk<Book, number, { state: RootState }>(
  'catalog/fetchBookAsync',
  async (bookId: number, thunkAPI) => {
    try {
      return await agent.Catalog.details(bookId);
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ error: error.data })
    }
  }
)

export const fetchFiltersAsync = createAsyncThunk(
  'catalog/fetchFiltersAsync',
  async (_, thunkAPI) => {
    try {
      return await agent.Catalog.fetchFilters();
    } catch (error: any) {
      return thunkAPI.rejectWithValue({ error: error.data });
    }
  }
)

const initParams = () => {
  return {
    pageNumber: 1,
    pageSize: 12,
    bookFormats: [],
    bookTypes: [],
    genres: [],
    languages: [],
    targetAudiences: [],
    countries: [],
    yearsOfPublication: [],
  }
}

export const catalogSlice = createSlice({
  name: 'catalog',
  initialState: booksAdapter.getInitialState<CatalogState>({
    initialLoad: true,
    booksLoaded: false,
    filtersLoaded: false,
    status: 'idle',
    bookFormats: [],
    bookTypes: [],
    genres: [],
    languages: [],
    targetAudiences: [],
    countries: [],
    yearsOfPublication: [],
    bookParams: initParams(),
    metaData: null
  }),
  reducers: {
    setBookParams: (state, action: PayloadAction<Partial<BookParams>>) => {
      state.booksLoaded = false;
      state.bookParams = { ...state.bookParams, ...action.payload, pageNumber: 1 };
    },
    setPageNumber: (state, action: PayloadAction<PaginationParams>) => {
      state.booksLoaded = false;
      state.bookParams = { ...state.bookParams, ...action.payload };
    },
    setMetaData: (state: any, action: any) => {
      state.metaData = action.payload;
    }
  },
  extraReducers: (builder => {
    builder.addCase(fetchBooksAsync.pending, (state) => {
      state.status = 'pendingFetchBooks';
    });
    builder.addCase(fetchBooksAsync.fulfilled, (state, action) => {
      booksAdapter.setAll(state, action.payload);
      state.status = 'idle';
      state.booksLoaded = true;
      state.initialLoad = false;
    });
    builder.addCase(fetchBooksAsync.rejected, (state, action) => {
      console.log(action);
      state.status = 'idle';
    });
    builder.addCase(fetchBookAsync.pending, (state) => {
      state.status = 'pendingFetchBook';
    });
    builder.addCase(fetchBookAsync.fulfilled, (state, action) => {
      booksAdapter.upsertOne(state, action.payload);
      state.status = 'idle';
    });
    builder.addCase(fetchBookAsync.rejected, (state, action) => {
      console.log(action);
      state.status = 'idle';
    });
    builder.addCase(fetchFiltersAsync.pending, (state) => {
      state.status = 'pendingFetchFilters';
    });
    builder.addCase(fetchFiltersAsync.fulfilled, (state, action) => {
      state.bookFormats = action.payload.bookFormatFilters;
      state.bookTypes = action.payload.bookTypeFilters.filter((t: any) => t !== 'NotSelected');
      state.genres = action.payload.genreFilters.filter((g: any) => g.genre !== 'Other').map((g: any) => `${g.displayName}|${g.genre}`).sort();
      state.languages = action.payload.languageFilters.map((l: any) => `${l.displayName}|${l.language}`).sort();
      state.targetAudiences = action.payload.targetAudienceFilters.filter((t: any) => t.targetAudience !== '21').map((t: any) => `${t.displayName}|${t.targetAudience}`).sort();
      state.countries = action.payload.countryFilters.filter((c: any) => c !== '(Unknown)');
      state.yearsOfPublication = action.payload.yearOfPublicationFilters;
      state.filtersLoaded = true;
      state.status = 'idle';
    });
    builder.addCase(fetchFiltersAsync.rejected, (state, action) => {
      console.log(action);
      state.status = 'idle';
    });
  })
})

export const bookSelectors = booksAdapter.getSelectors((state: RootState) => state.catalog);
export const { setBookParams, setPageNumber, setMetaData } = catalogSlice.actions;
